/**
 * 历史图库 / 我的资产面板（设计稿 235:226）
 *
 * - 1012x688 主面板：图片资产 / 视频资产 / 音频资产 三 tab
 * - 单选（已选 N/1 张），180x240 卡片 5 列网格，图片/视频滚动浏览（无分页）
 * - 底部：已选计数 + 确定按钮（回传选中资产）
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Music, Film } from 'lucide-react';
import type { LibraryAsset } from '@/stores/worksStore';

/* ============ 常量 ============ */

/** 卡片颜色（设计稿 235:226） */
const PANEL_BG = '#1D1D1D';

/** 网格滚动条（深色细滚动条，组件内样式模式参照 skill-picker-scroll） */
const SCROLLBAR_STYLE = `
.assets-scroll::-webkit-scrollbar { width: 8px; }
.assets-scroll::-webkit-scrollbar-track { background: transparent; }
.assets-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.18); border-radius: 4px; }
.assets-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
.assets-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.18) transparent; }
`;

export interface HistoryAssetsPanelProps {
  open: boolean;
  /** 当前允许选择的资产类型（图片作品→image / 视频作品→video） */
  acceptType: 'image' | 'video';
  /** 全量资产（已由父级合并 assetHistory ∪ store.libraryAssets） */
  assets: LibraryAsset[];
  onClose: () => void;
  onConfirm: (picked: LibraryAsset[]) => void;
  /** 点击「上传」占位卡时触发（转回主弹窗本地上传） */
  onUploadClick?: () => void;
}

/* ============ 主组件 ============ */

export function HistoryAssetsPanel({ open, acceptType, assets, onClose, onConfirm, onUploadClick }: HistoryAssetsPanelProps) {
  /** 当前 tab：image/video/audio */
  const [tab, setTab] = useState<'image' | 'video' | 'audio'>(acceptType);
  /** 单选 id */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** 网格滚动容器：切 tab / acceptType 变化时回到顶部 */
  const gridScrollRef = useRef<HTMLDivElement>(null);

  /* —— acceptType 变化时同步 tab 并回滚顶部 —— */
  useEffect(() => { if (open) { setTab(acceptType); setSelectedId(null); if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0; } }, [open, acceptType]);


  /* —— Esc 关闭 —— */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /* —— 按 tab 过滤（滚动浏览，无分页） —— */
  const filtered = useMemo(() => assets.filter((a) => a.type === tab), [assets, tab]);

  /** 类型是否允许选择（音频仅浏览） */
  const selectable = tab === acceptType;

  const handleConfirm = useCallback(() => {
    const hit = selectedId ? assets.find((a) => a.id === selectedId) : null;
    onConfirm(hit && hit.type === acceptType ? [hit] : []);
  }, [assets, selectedId, acceptType, onConfirm]);

  if (!open) return null;


  return (
    <div className="fixed inset-0" style={{ zIndex: 1010 }}>
      {/* 遮罩（比上传弹窗更高层级，覆盖其上） */}
      <div className="absolute inset-0 bg-[#121212]/70 backdrop-blur-[8px]" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="pointer-events-auto relative flex flex-col overflow-hidden"
          /* 流式自适应：宽窗（≥1044px）下与原 1012×688 完全一致；窄窗/矮窗时收缩到视口内
            （四周边距 32px），内部网格滚动区弹性消化高度，字号恒定 */
          style={{ width: 'min(1012px, 100vw - 32px)', height: 'min(688px, 100vh - 32px)', backgroundColor: PANEL_BG, borderRadius: 16, border: '1px solid #2D2D2D' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* —— 头部：标题 + 关闭（设计稿 @28,32 / @956,24 icon_GB 白15%圆底+X 32x32） —— */}
          <div className="relative flex items-center justify-between pl-7 pr-6 pt-8">
            <div className="text-[16px] font-semibold text-white/45 leading-none">我的资产</div>
            <button
              type="button"
              aria-label="关闭"
              className="w-8 h-8 flex items-center justify-center transition-opacity hover:opacity-70"
              onClick={onClose}
            >
              <img src="/icons/upload-work/icon-close.svg" alt="" width={32} height={32} className="pointer-events-none" />
            </button>
          </div>

          {/* —— 三 Tab + 分割线（设计稿 y=81/111；已选计数移至底栏左侧） —— */}
          <div className="px-7 pt-6 flex items-end">
            <div className="flex items-center gap-[24px]">
              {([['image', '图片资产'], ['video', '视频资产'], ['audio', '音频资产']] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="relative pb-3 text-[14px] leading-none transition-colors"
                  style={{ color: tab === key ? '#FFFFFF' : 'rgba(255,255,255,0.45)' }}
                  onClick={() => { setTab(key); setSelectedId(null); if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0; }}
                >
                  {label}
                  {tab === key && <span className="absolute left-0 right-0 -bottom-px h-px bg-white" />}
                </button>
              ))}
            </div>
          </div>
          <div className="mx-6 mt-px h-px bg-white/15" />


          {/* —— 卡片网格 5 列 180x240（设计稿 @y144 x=24,220,416,612,808），滚动浏览 —— */}
          <style>{SCROLLBAR_STYLE}</style>
          <div ref={gridScrollRef} className="assets-scroll flex-1 min-h-0 px-6 pt-[33px] pb-6 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/35">
                <Film size={32} strokeWidth={1} />
                <span className="text-[13px]">暂无{tab === 'image' ? '图片' : tab === 'video' ? '视频' : '音频'}资产</span>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-4">
                {filtered.map((a) => {
                  const on = selectable && selectedId === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={`relative w-full h-[240px] rounded-2xl overflow-hidden bg-[#2D2D2D] text-left transition-shadow ${selectable ? 'cursor-pointer hover:ring-1 hover:ring-white/40' : 'cursor-default opacity-60'}`}
                      style={{ boxShadow: on ? '0 0 0 2px #FFFFFF inset' : undefined }}
                      onClick={() => { if (selectable) setSelectedId(on ? null : a.id); }}
                    >
                      {a.type === 'image' ? (
                        <img src={a.src} alt={a.title} className="w-full h-full object-cover" />
                      ) : a.type === 'video' ? (
                        <video src={a.src} className="w-full h-full object-cover" preload="metadata" muted />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/50">
                          <Music size={36} strokeWidth={1} />
                          <span className="text-[12px] px-3 truncate w-full text-center">{a.title}</span>
                        </div>
                      )}
                      {/* 选中角标（设计稿 icon_DX 24x24） */}
                      {on && (
                        <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white flex items-center justify-center">
                          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 6.2 5 8.7 9.5 3.6" stroke="#121212" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </span>
                      )}
                      {/* 底部标题渐变条 */}
                      <span className="absolute left-0 right-0 bottom-0 h-10 bg-gradient-to-t from-black/70 to-transparent flex items-end px-2.5 pb-1.5">
                        <span className="text-[11px] text-white/85 truncate">{a.title}</span>
                      </span>
                    </button>
                  );
                })}
                {/* 上传占位卡（设计稿第5卡：#2D2D2D 实底圆角12 + 图片图标居中，点击转回本地上传） */}
                {selectable && onUploadClick && (
                  <button
                    type="button"
                    aria-label="上传"
                    className="w-full h-[240px] rounded-xl bg-[#2D2D2D] flex items-center justify-center hover:bg-[#333333] transition-colors"
                    onClick={onUploadClick}
                  >
                    <img src="/icons/upload-work/icon-empty-image.svg" alt="" width={32} height={32} className="pointer-events-none" />
                  </button>
                )}
              </div>
            )}
          </div>


          {/* —— 底部栏 112px（设计稿 @0,576）：左已选计数 + 右确定按钮 —— */}
          <div className="h-[112px] shrink-0 border-t border-white/10 flex items-center justify-between px-6">
            {/* 已选计数（设计稿 @24,625 14px 白65%） */}
            <div className="text-[14px] text-white/65 leading-none">
              {selectable ? <>已选 {selectedId ? 1 : 0}/1 张</> : '该类型仅支持浏览'}
            </div>
            {/* 确定按钮（设计稿 @788,608 200x48） */}
            <button
              type="button"
              className="w-[200px] h-12 rounded-xl bg-white text-[#121212] text-[16px] font-medium transition-opacity disabled:opacity-25 disabled:cursor-not-allowed hover:opacity-90"
              disabled={!selectedId}
              onClick={handleConfirm}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
